import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CrmLayout } from "@/components/layouts/CrmLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Skeleton } from "@/components/ui";
import { HomePage } from "@/pages/command/HomePage";
import { ContactsPage } from "@/pages/ContactsPage";
import { AccountsPage } from "@/pages/AccountsPage";
import { ContactDetailPage } from "@/pages/ContactDetailPage";
import { ListsPage } from "@/pages/ListsPage";
import { InboxPage } from "@/pages/InboxPage";
import { MailPage } from "@/pages/MailPage";
import { CampaignsPage } from "@/pages/CampaignsPage";
import { FunnelPage } from "@/pages/FunnelPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { SourcesPage } from "@/pages/command/SourcesPage";
import { SourceDetailPage } from "@/pages/command/SourceDetailPage";
import { AffiliatesPage } from "@/pages/command/AffiliatesPage";
import { AffiliateDetailPage } from "@/pages/command/AffiliateDetailPage";
import { WorkPage } from "@/pages/command/WorkPage";
import { WorkCalendarPage } from "@/pages/command/WorkCalendarPage";
import { SchedulingLinksPage } from "@/pages/SchedulingLinksPage";
import { GoalsPage } from "@/pages/command/GoalsPage";
import { TeamPage } from "@/pages/command/TeamPage";

// Lazy-load the heavy chart/graph routes so xyflow + dagre (~270 KB) and
// recharts (~110 KB) don't ship to sessions that never open them.
const AutomationsPage = lazy(() => import("@/pages/AutomationsPage").then((m) => ({ default: m.AutomationsPage })));
const JourneysPage    = lazy(() => import("@/pages/JourneysPage").then((m) => ({ default: m.JourneysPage })));
// Website analytics (recharts).
const DialerPage      = lazy(() => import("@/pages/DialerPage"));
// Social planner (ported from The Prima Club CRM).
const SocialPage         = lazy(() => import("@/pages/social/SocialPage").then((m) => ({ default: m.SocialPage })));
const SocialCalendarPage = lazy(() => import("@/pages/social/SocialCalendarPage").then((m) => ({ default: m.SocialCalendarPage })));
// Growth areas (2026-10-28): one system per kind of traffic.
const AdsPage       = lazy(() => import("@/pages/growth/AdsPage").then((m) => ({ default: m.AdsPage })));
const OutreachPage  = lazy(() => import("@/pages/growth/OutreachPage").then((m) => ({ default: m.OutreachPage })));
const PartnersPage  = lazy(() => import("@/pages/growth/PartnersPage").then((m) => ({ default: m.PartnersPage })));
const CreatorsPage  = lazy(() => import("@/pages/growth/CreatorsPage").then((m) => ({ default: m.CreatorsPage })));
// Customers → AI-Ready Day (2026-10-28): the free live event.
const AiReadyDayPage = lazy(() => import("@/pages/AiReadyDayPage").then((m) => ({ default: m.AiReadyDayPage })));

function PageSpinner() {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
    </div>
  );
}

const lazyPage = (el: React.ReactNode) => <Suspense fallback={<PageSpinner />}>{el}</Suspense>;

export function App() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route element={<CrmLayout />}>
          {/* Home: the command center */}
          <Route path="/"                element={<HomePage />} />

          {/* Inbox */}
          <Route path="/inbox"           element={<InboxPage />} />
          <Route path="/inbox/:contactId" element={<InboxPage />} />
          <Route path="/mail"            element={<MailPage />} />
          <Route path="/mail/:threadId"  element={<MailPage />} />
          <Route path="/campaigns"       element={<CampaignsPage />} />
          <Route path="/campaigns/:id"   element={<CampaignsPage />} />
          <Route path="/lists"           element={<ListsPage />} />

          {/* Customers */}
          <Route path="/contacts"        element={<ContactsPage />} />
          <Route path="/contacts/:id"    element={<ContactDetailPage />} />
          <Route path="/accounts"        element={<AccountsPage />} />
          <Route path="/accounts/:id"    element={<AccountsPage />} />
          <Route path="/funnel"          element={<FunnelPage />} />
          <Route path="/ai-ready-day"    element={lazyPage(<AiReadyDayPage />)} />

          {/* Growth */}
          <Route path="/traffic"         element={<SourcesPage />} />
          <Route path="/traffic/:id"     element={<SourceDetailPage />} />
          <Route path="/affiliates"      element={<AffiliatesPage />} />
          <Route path="/affiliates/:id"  element={<AffiliateDetailPage />} />
          <Route path="/ads"             element={lazyPage(<AdsPage />)} />
          <Route path="/outreach"        element={lazyPage(<OutreachPage />)} />
          <Route path="/partners"        element={lazyPage(<PartnersPage />)} />
          <Route path="/partners/:id"    element={lazyPage(<PartnersPage />)} />
          <Route path="/creators"        element={lazyPage(<CreatorsPage />)} />
          <Route path="/creators/:id"    element={lazyPage(<CreatorsPage />)} />
          <Route path="/content"         element={lazyPage(<SocialPage />)} />
          <Route path="/content/calendar" element={lazyPage(<SocialCalendarPage />)} />
          {/* Website analytics live in Traffic sources' Website tab (2026-10-28). */}
          <Route path="/website"         element={<WebsiteRedirect />} />

          {/* Work */}
          <Route path="/work"            element={<WorkPage />} />
          <Route path="/work/calendar"   element={<WorkCalendarPage />} />
          <Route path="/work/goals"      element={<GoalsPage />} />
          <Route path="/work/booking"    element={<SchedulingLinksPage />} />
          <Route path="/team"            element={<TeamPage />} />

          {/* Automations */}
          <Route path="/journeys"        element={lazyPage(<JourneysPage />)} />
          <Route path="/automations"     element={lazyPage(<AutomationsPage />)} />
          <Route path="/automations/:id" element={lazyPage(<AutomationsPage />)} />

          <Route path="/settings"        element={<SettingsPage />} />

          {/* Old addresses (bookmarks, links in emails) land somewhere useful. */}
          <Route path="/tasks"            element={<Navigate to="/work" replace />} />
          <Route path="/calendar"         element={<Navigate to="/work/calendar" replace />} />
          <Route path="/scheduling-links" element={<Navigate to="/work/booking" replace />} />
          <Route path="/pipelines"        element={<Navigate to="/funnel?view=board" replace />} />
          <Route path="/social"           element={<Navigate to="/content" replace />} />
          <Route path="/social/calendar"  element={<Navigate to="/content/calendar" replace />} />
          <Route path="/reports"          element={<Navigate to="/" replace />} />
          <Route path="/payments/*"       element={<Navigate to="/" replace />} />
          <Route path="/store/*"          element={<Navigate to="/" replace />} />
          <Route path="/dialer"           element={lazyPage(<DialerPage />)} />

          {/* 404 catch-all — inside CrmLayout so the nav stays. */}
          <Route path="*"                element={<NotFoundPage />} />
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}

/** /website moved into Traffic sources (Website tab); People stays People. */
function WebsiteRedirect() {
  const { search } = useLocation();
  const p = new URLSearchParams(search);
  const next = new URLSearchParams({ tab: "website" });
  if (p.get("tab") === "people" || p.get("view") === "people") next.set("view", "people");
  return <Navigate to={`/traffic?${next.toString()}`} replace />;
}
